<!-- 
  @file PageLayout.vue
  @description
  Core layout component that provides a standardized page structure across the application.
  Handles navigation, vehicle information display, and privacy policy presentation.

  @features
  - Responsive layout with mobile and desktop support
  - Conditional vehicle details rendering
  - Automated back navigation handling
  - First-page privacy policy display
  - Dynamic header management
  - Theme-aware styling
  - Accessibility compliant structure

  @props
  - currentPage: PageName (required) - Identifier for the current page in the application flow
  - header?: string - Optional page title/heading text
  - showBenefits?: boolean - Controls visibility of benefits section in privacy policy

  @components
  - BackButton: Handles navigation to previous page
  - VehicleDetails: Displays vehicle information when available
  - PrivacyPolicy: Renders privacy policy on flow entry pages

  @stores
  - vehicleStore: Manages vehicle data state
  - configStore: Handles application configuration and theming
  - progressStore: Controls flow progression and form state

  @slots
  - default: Main content slot for page-specific components

  @example
  ```vue
  <PageLayout 
    current-page="vehicle-details"
    header="Vehicle Information"
    :showBenefits="true"
  >
    <VehicleForm />
  </PageLayout>
  ```

  @accessibility
  - Semantic HTML structure
  - ARIA-compliant navigation
  - Proper heading hierarchy
  - Responsive layout considerations
-->
<template>
  <div>
    <BackButton
      v-if="shouldShowBackButton"
      :text="(contents as any).NAVIGATION_CONTENT.PREVIOUS_STEP"
    />

    <VehicleDetails
      v-if="vehicle.vrm" 
      :show-vehicle-image="false" 
      :vehicle="vehicle"
      :show-vehicle-specs="false"
      :show-vehicle-card-border="false"
    />

    <hr
      v-if="vehicle.vrm" 
      class="absolute md:static w-full border-t border-[#DDDDDD] left-0 right-0 md:mt-0"
    >

    <h1 v-if="header" class="text-h2 mb-2" :class="[vehicle?.vrm ? 'mt-6' : 'mt-3']">{{ header }}</h1>

    <slot></slot>

    <PrivacyPolicy 
      v-if="progressStore.formType && FLOW_ROUTES[progressStore.formType].pages[0] === currentPage" 
      :showBenefits="showBenefits" 
    />

    <FinanceDisclaimer class="mt-4" 
      v-if="progressStore.formType && FLOW_ROUTES[progressStore.formType].pages[0] === currentPage" 
      :show-commission="false" />
  </div>
</template>

<script setup lang="ts">
/**
 * @component PageLayout
 * @description Provides a consistent layout structure for all pages in the application flow
 */
import { computed } from 'vue';
import { useVehicleStore } from '@/stores/modules/vehicleStore';
import { useConfigStore } from '@/stores/modules/configStore';
import { useProgressStore } from '@/stores/modules/progressStore';
import { FLOW_ROUTES, STANDARD_SEQUENCE } from '@/constants/flows';
import type { PageName } from '@/constants/flows';
import BackButton from '@/components/BackButton.vue';
import VehicleDetails from '@/components/VehicleDetails.vue';
import PrivacyPolicy from '@/components/PrivacyPolicy.vue';
import { useRoute } from 'vue-router';
import FinanceDisclaimer from '@/components/FinanceDisclaimer.vue';

/**
 * Component props
 * @typedef {Object} Props
 * @property {PageName} currentPage - Current page identifier for navigation and flow control
 * @property {string} [header] - Optional header text to display at the top of the page
 */
const { currentPage, header, showBenefits } = defineProps<{
  currentPage: PageName;
  header?: string;
  showBenefits?: boolean;
}>();

/**
 * Vehicle store instance and computed vehicle data
 * @type {import('@vue/reactivity').ComputedRef<Vehicle>}
 */
const vehicleStore = useVehicleStore();
const vehicle = computed(() => vehicleStore.vehicle);

/**
 * Progress store instance for flow management
 * @type {import('@/stores/modules/progressStore').ProgressStore}
 */
const progressStore = useProgressStore();

/**
 * Navigation content from config store
 * @type {Object}
 */
const { contents } = useConfigStore().affiliateConfig;

/**
 * Route instance for accessing query parameters
 * @type {import('@/router').RouteLocationNormalizedLoaded}
 */
const route = useRoute();

/**
 * Determines if back button should be shown based on current page position in flow
 * @type {import('@vue/reactivity').ComputedRef<boolean>}
 */
const shouldShowBackButton = computed(() => {
  // For prepopulated forms, check if there's a valid previous page in the sequence
  if (progressStore.formType === 'prePopulated') {
    const pagesToComplete = progressStore.pagesToComplete;
    
    if (pagesToComplete && pagesToComplete.length > 0) {
      const currentPageIndex = pagesToComplete.indexOf(currentPage);
      
      // Hide back button if:
      // 1. Current page is not in the pagesToComplete array (edge case)
      // 2. Current page is the first in the sequence (index 0)
      // 3. No previous page exists
      if (currentPageIndex <= 0) {
        return false;
      }
      
      // Show back button if there's a valid previous page in the sequence
      return true;
    }
    
    // If no pagesToComplete array, fall back to original logic
    if (progressStore.previousPage === '') {
      return false;
    }
  }
  
  // For non-prepopulated forms, use the original flow-based logic
  const basePage = currentPage.split('/')[0] as PageName;
  const flowPages = FLOW_ROUTES[progressStore.formType || 'direct'].pages;
  const pageIndex = flowPages.indexOf(basePage);
  
  return pageIndex > 0;
});
</script> 